// Dashboard time windows. Single owner of the period vocabulary shared by the
// API (query validation, window math) and the web app (route search params,
// period picker, trend captions) — the two sides must accept exactly the same
// values, and nothing else enforces that.

export const DASHBOARD_PERIODS = ["7d", "14d", "30d", "90d", "12m"] as const

export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number]

/** The window the dashboards open on, and the API's fallback when a request
 *  omits `?period=`. Owned here, next to the vocabulary, so the route
 *  search-param default and the query fallback can't drift apart — a browser
 *  hitting the API without a period must get the view the UI opens on. */
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = "30d"

export const DASHBOARD_DATE_SELECTION_KIND = {
  rolling: "rolling",
  month: "month",
  custom: "custom",
} as const

export type DashboardDateSelection =
  | { kind: typeof DASHBOARD_DATE_SELECTION_KIND.rolling; period: DashboardPeriod }
  | { kind: typeof DASHBOARD_DATE_SELECTION_KIND.month; month: string }
  | { kind: typeof DASHBOARD_DATE_SELECTION_KIND.custom; from: string; to: string }

export type DashboardDateQuery =
  | { period: DashboardPeriod }
  | { month: string }
  | { from: string; to: string }

export type DashboardDateQueryInput = {
  period?: string
  month?: string
  from?: string
  to?: string
}

export const DEFAULT_DASHBOARD_DATE_SELECTION: DashboardDateSelection = {
  kind: DASHBOARD_DATE_SELECTION_KIND.rolling,
  period: DEFAULT_DASHBOARD_PERIOD,
}

/** The inclusive UTC bounds of a calendar week, Monday to Sunday.
 *  `offsetWeeks` counts back: 0 is the week in progress, 1 is last week.
 *
 *  Lives here, beside the rest of the window math, even though only the campaign
 *  table asks for it (#482). The card sends the result as a custom from/to
 *  rather than a fourth selection kind — the wire vocabulary every read and
 *  route switches on stays three-valued — but the definition of "which days are
 *  last week" must not be a second implementation sitting in a component.
 *
 *  No UI offers the week IN PROGRESS any more: its window is clamped to today
 *  while the ads sync only imports through yesterday, so on Monday it held no
 *  importable day at all and the card rendered "no ad report". `offsetWeeks = 0`
 *  stays a legitimate input of this helper — the clamp below is a property of
 *  the math, not of the picker that used to call it.
 *
 *  Monday, not Sunday: the ads review runs Monday morning, so a week that ends
 *  the day before it is the one an operator is asked to judge.
 *
 *  The CURRENT week is clamped to today — see the note on the return. */
export function calendarWeekRange(
  now: Date = new Date(),
  offsetWeeks = 0
): { from: string; to: string } {
  const day = startOfUtcDay(now)
  // getUTCDay is 0 for Sunday; shift so Monday is 0 and the week ends Sunday.
  const sinceMonday = (day.getUTCDay() + 6) % 7
  const monday = day.getTime() - (sinceMonday + offsetWeeks * 7) * 86_400_000
  const sunday = monday + 6 * 86_400_000
  // CLAMPED to today. The week in progress ends in the future on every day but
  // Sunday, and a range that ends in the future is refused by
  // `parseDashboardDateSelection` — so an unclamped "this week" asked the API for
  // something it must reject, and the card rendered the refusal as "nothing was
  // advertised". The bounds this returns are the queryable part of the week.
  return { from: utcDay(monday), to: utcDay(Math.min(sunday, day.getTime())) }
}

/** The custom picker exposes at most 366 inclusive UTC calendar days. */
export const DASHBOARD_HISTORY_DAYS = 366

const DAY_MS = 86_400_000
const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTH_PATTERN = /^(\d{4})-(\d{2})$/

export type DashboardDateSelectionParseResult =
  | { ok: true; value: DashboardDateSelection }
  | { ok: false; error: string }

/** Parses the three mutually-exclusive dashboard URL forms. Temporal bounds
 * are UTC-calendar bounds, matching the server-owned reporting windows. */
export function parseDashboardDateSelection(
  query: DashboardDateQueryInput,
  now: Date = new Date()
): DashboardDateSelectionParseResult {
  const hasPeriod = query.period !== undefined
  const hasMonth = query.month !== undefined
  const hasFrom = query.from !== undefined
  const hasTo = query.to !== undefined
  const modeCount = Number(hasPeriod) + Number(hasMonth) + Number(hasFrom || hasTo)

  if (modeCount === 0) {
    return { ok: true, value: DEFAULT_DASHBOARD_DATE_SELECTION }
  }
  if (modeCount !== 1) {
    return { ok: false, error: "Use exactly one date form: period, month, or from with to." }
  }
  if (hasPeriod) {
    return parseRollingSelection(query.period)
  }
  if (hasMonth) {
    return parseMonthSelection(query.month, now)
  }

  return parseCustomSelection(query, now)
}

function parseRollingSelection(period: string | undefined): DashboardDateSelectionParseResult {
  if (!period || !isDashboardPeriod(period)) {
    return {
      ok: false,
      error: `Unknown period: ${period ?? ""}. Use one of ${DASHBOARD_PERIODS.join(", ")}.`,
    }
  }
  return {
    ok: true,
    value: { kind: DASHBOARD_DATE_SELECTION_KIND.rolling, period },
  }
}

function parseMonthSelection(
  monthRaw: string | undefined,
  now: Date
): DashboardDateSelectionParseResult {
  const month = monthRaw ?? ""
  const monthStart = parseUtcMonth(month)
  if (!monthStart) {
    return { ok: false, error: `Invalid month: ${month}. Use YYYY-MM.` }
  }
  const currentMonthMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
  const earliestMonthMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11, 1)
  if (monthStart.getTime() > currentMonthMs) {
    return { ok: false, error: `Future month is unavailable: ${month}.` }
  }
  if (monthStart.getTime() < earliestMonthMs) {
    return { ok: false, error: `Month is outside the available 12-month history: ${month}.` }
  }
  return { ok: true, value: { kind: DASHBOARD_DATE_SELECTION_KIND.month, month } }
}

function parseCustomSelection(
  query: DashboardDateQueryInput,
  now: Date
): DashboardDateSelectionParseResult {
  const hasFrom = query.from !== undefined
  const hasTo = query.to !== undefined

  if (!hasFrom || !hasTo) {
    return { ok: false, error: "Custom ranges require both from and to." }
  }
  const from = query.from ?? ""
  const to = query.to ?? ""
  const fromDate = parseUtcDay(from)
  const toDate = parseUtcDay(to)
  if (!fromDate || !toDate) {
    return { ok: false, error: "Invalid custom range. Use real YYYY-MM-DD dates." }
  }
  const fromMs = fromDate.getTime()
  const toMs = toDate.getTime()
  const todayMs = startOfUtcDay(now).getTime()
  const earliestMs = todayMs - (DASHBOARD_HISTORY_DAYS - 1) * DAY_MS
  if (fromMs > toMs) {
    return { ok: false, error: "Custom range start must be on or before its end." }
  }
  if (toMs > todayMs) {
    return { ok: false, error: "Custom range cannot end in the future." }
  }
  if (fromMs < earliestMs) {
    return {
      ok: false,
      error: `Custom range is outside the available ${DASHBOARD_HISTORY_DAYS}-day history.`,
    }
  }
  if ((toMs - fromMs) / DAY_MS + 1 > DASHBOARD_HISTORY_DAYS) {
    return {
      ok: false,
      error: `Custom range cannot exceed ${DASHBOARD_HISTORY_DAYS} inclusive days.`,
    }
  }
  return { ok: true, value: { kind: DASHBOARD_DATE_SELECTION_KIND.custom, from, to } }
}

export function dashboardDateQuery(selection: DashboardDateSelection): DashboardDateQuery {
  switch (selection.kind) {
    case DASHBOARD_DATE_SELECTION_KIND.rolling:
      return { period: selection.period }
    case DASHBOARD_DATE_SELECTION_KIND.month:
      return { month: selection.month }
    case DASHBOARD_DATE_SELECTION_KIND.custom:
      return { from: selection.from, to: selection.to }
  }
}

/** Stable primitive tuple for TanStack Query keys. */
export function dashboardDateSelectionKey(
  selection: DashboardDateSelection
): readonly [string, string, string?] {
  switch (selection.kind) {
    case DASHBOARD_DATE_SELECTION_KIND.rolling:
      return [selection.kind, selection.period]
    case DASHBOARD_DATE_SELECTION_KIND.month:
      return [selection.kind, selection.month]
    case DASHBOARD_DATE_SELECTION_KIND.custom:
      return [selection.kind, selection.from, selection.to]
  }
}

export function dashboardAvailableDayRange(now: Date = new Date()): InclusiveDayRange {
  const todayMs = startOfUtcDay(now).getTime()
  return {
    from: utcDay(todayMs - (DASHBOARD_HISTORY_DAYS - 1) * DAY_MS),
    to: utcDay(todayMs),
  }
}

export function dashboardSelectionLabel(
  selection: DashboardDateSelection,
  now: Date = new Date()
): string {
  if (selection.kind === DASHBOARD_DATE_SELECTION_KIND.rolling) {
    return DASHBOARD_PERIOD_LABELS[selection.period]
  }
  if (selection.kind === DASHBOARD_DATE_SELECTION_KIND.month) {
    const month = parseUtcMonth(selection.month)
    if (!month) {
      return selection.month
    }
    const label = MONTH_LABEL_FORMAT.format(month)
    const isCurrent =
      month.getUTCFullYear() === now.getUTCFullYear() && month.getUTCMonth() === now.getUTCMonth()
    return isCurrent ? `${label} to date` : label
  }
  return formatCustomRangeLabel(selection.from, selection.to)
}

export const DASHBOARD_PERIOD_LABELS = {
  "7d": "Last 7 days",
  "14d": "Last 14 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "12m": "Last 12 months",
} satisfies Record<DashboardPeriod, string>

const MONTH_LABEL_FORMAT = new Intl.DateTimeFormat("en-GB", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
})
const SHORT_DAY_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
})
const SHORT_DAY_YEAR_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
})

function formatCustomRangeLabel(from: string, to: string): string {
  const fromDate = parseUtcDay(from)
  const toDate = parseUtcDay(to)
  if (!fromDate || !toDate) {
    return `${from} – ${to}`
  }
  if (from === to) {
    return SHORT_DAY_YEAR_FORMAT.format(fromDate)
  }
  const fromLabel = SHORT_DAY_FORMAT.format(fromDate)
  const toLabel = SHORT_DAY_YEAR_FORMAT.format(toDate)
  return `${fromLabel} – ${toLabel}`
}

function parseUtcDay(value: string): Date | null {
  const parts = DAY_PATTERN.exec(value)
  if (!parts) {
    return null
  }
  const year = Number(parts[1])
  const month = Number(parts[2])
  const day = Number(parts[3])
  const parsed = new Date(Date.UTC(year, month - 1, day))
  return utcDay(parsed.getTime()) === value ? parsed : null
}

function parseUtcMonth(value: string): Date | null {
  const parts = MONTH_PATTERN.exec(value)
  if (!parts) {
    return null
  }
  const year = Number(parts[1])
  const month = Number(parts[2])
  if (month < 1 || month > 12) {
    return null
  }
  const parsed = new Date(Date.UTC(year, month - 1, 1))
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, "0")}` === value
    ? parsed
    : null
}

function startOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
}

export function isDashboardPeriod(value: string): value is DashboardPeriod {
  return (DASHBOARD_PERIODS as readonly string[]).includes(value)
}

/** An inclusive UTC calendar-day window — the vocabulary every day-ranged API
 *  (payout history, payout sync) speaks. */
export type InclusiveDayRange = { from: string; to: string }

/** A dashboard window is the half-open instant pair `[start, end)` the API
 *  computes for a period. Converting it to an inclusive day range — the last
 *  covered day is the one before `end` — belongs here, with the rest of the
 *  period vocabulary, so no feature hook re-derives it and drifts by a day.
 *  Returns null when either bound is missing or unparseable. */
export function inclusiveDayRangeFromWindow(window: {
  start: string | undefined
  end: string | undefined
}): InclusiveDayRange | null {
  if (!window.start || !window.end) {
    return null
  }
  const start = Date.parse(window.start)
  const end = Date.parse(window.end)
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return null
  }
  return { from: utcDay(start), to: utcDay(end - 86_400_000) }
}

/** The one instant → UTC calendar-day formatter behind {@link InclusiveDayRange}.
 *  Exported so anything building a day range (the CLI's trailing sync windows)
 *  formats its bounds exactly the way the dashboard's do. */
export function utcDay(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10)
}
